import { Box, Button, Container, Divider, Grid, InputAdornment, Skeleton, TextField } from '@mui/material'
import CustomFilterButton from 'components/Fileds/CustomFilterButton'
import OrderSummary from 'components/OrderSummary/OrderSummary'
import POSHeader from 'components/POSHeader'
import RestaurantInfo from 'components/RestaurantInfo/RestaurantInfo'
import React, { useMemo, useEffect } from 'react'
import { ThemeProvider } from 'theme/ThemeContext2'
import { ordersFilterConfigs } from 'utils/constant'
import { isOrderTypeFlagAllowed } from 'utils/function'
import HistoryIcon from '@mui/icons-material/History'
import SearchIcon from '@mui/icons-material/Search'
import CustomSearch from 'components/Fileds/CustomSearch'
import CommonDataGrid from 'components/TableListing/CommonTable'
import "./style.css";
import NoSearchFound from 'components/NoSearchFound/NoSearchFound'
import NoItemsCreated from 'components/NoItemsCreated/NoItemsCreated'
import NoOrders from "assests/empty-order.svg";
import NoSearchOrder from "assests/no-search-order.svg";

const OrdersConatiner = ({ orderSummaryDetails, userData, setFilter, filter, searchQuery,
    columns, rows, totalCount, loading, paginationModel, setPaginationModel, setSearchQuery, searchLoader, handleViewPastOrders,
    orderTypeFlags
}) => {
    // Show skeleton while initial order type flags are loading to avoid button flash
    const isFlagsLoading = !orderTypeFlags && loading;

    // Filter visible order type tab buttons based on API order_type_flags
    const visibleFilterConfigs = useMemo(() => {
        return ordersFilterConfigs.filter((f) => isOrderTypeFlagAllowed(f.id, orderTypeFlags));
    }, [orderTypeFlags]);

    // If currently selected filter becomes hidden, switch gracefully to 'All'
    useEffect(() => {
        if (filter && filter !== 'All' && visibleFilterConfigs.length > 0) {
            const isVisible = visibleFilterConfigs.some((f) => f.id === filter);
            if (!isVisible) {
                setFilter('All');
            }
        }
    }, [filter, visibleFilterConfigs, setFilter]);

    // Determine which empty state to show
    const showNoData = !loading && rows.length === 0;
    const isSearchEmpty = searchQuery.trim().length > 0;
    return (
        <ThemeProvider>
            <Box className='order-listing' sx={{ bgcolor: "#fff", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
                <POSHeader />
                <Box className="pos-container">
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={{
                            px: { xs: 1, sm: 2, md: 3 },
                            py: 2,
                            maxWidth: "1400px",
                            mx: "auto",
                        }}
                    >
                        <Grid container spacing={{ xs: 1, sm: 2 }}>
                            <Grid item xs={12} lg={9}>
                                <Box>
                                    <OrderSummary orderSummary={orderSummaryDetails} />
                                    <Divider sx={{ mt: 2 }} className="orders-responsive-width" />
                                </Box>
                            </Grid>

                            {/* <Grid item xs={12} lg={3}>
                                <RestaurantInfo name={userData?.vendor?.name} description={userData?.vendor?.tagline} />
                            </Grid> */}
                        </Grid>

                        <Box className='d-flex align-items-center justify-content-between' sx={{ mt: '20px', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: '15px' }}>
                                {isFlagsLoading ? (
                                    [55, 95, 80, 80, 105].map((w, index) => (
                                        <Skeleton
                                            key={index}
                                            variant="rectangular"
                                            animation="wave"
                                            width={w}
                                            height={30}
                                            sx={{ borderRadius: '5px' }}
                                        />
                                    ))
                                ) : (
                                    visibleFilterConfigs.map((f) => (
                                        <CustomFilterButton
                                            key={f.id}
                                            label={f.label}
                                            color={f.color}
                                            backgroundColor={f.backgroundColor}
                                            selected={filter === f.id}
                                            onClick={() => setFilter(f.id)}
                                        />
                                    ))
                                )}
                            </Box>

                            <Box className='d-flex align-items-center' sx={{ gap: '10px' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<HistoryIcon />}
                                    onClick={handleViewPastOrders}
                                    sx={{ textTransform: 'none', width: '200px', padding: '7px', borderRadius: '10px', lineHeight: '1.5' }}
                                >
                                    Past Orders
                                </Button>

                                <CustomSearch
                                    onSearchChange={setSearchQuery}
                                    searchQuery={searchQuery}
                                    placeholder={"Search Order"}
                                    isLoading={searchLoader}
                                />
                            </Box>
                        </Box>
                    </Container>
                </Box>
                <Box className='order-table'>
                    {showNoData ? (
                        isSearchEmpty ? (
                            <NoSearchFound
                                title="No orders that match your search!"
                                subtitle="Please try using different Order Number or Customer Name for searching."
                                img={NoSearchOrder}
                                width="250px"
                                height="250px"
                            />
                        ) : (
                            <NoItemsCreated
                                title="No orders to attend!"
                                subtitle="Take a breath and we will show as soon as we have received something!"
                                img={NoOrders}
                                width="250px"
                                height="250px"
                            />
                        )
                    ) : (
                        <CommonDataGrid
                            columns={columns}
                            rows={rows}
                            rowCount={totalCount}
                            loading={loading}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                        />
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    )
}

export default OrdersConatiner
